import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { actionColors } from '@koob/entity-history/src/constants';
import {
  IGNORED_FIELDS,
  normalizeTimeValue,
} from '@koob/entity-history/src/components/FieldDiff/helpers';
import { formatDateTime } from '@koob/dates';

const CHILDREN_PREFIXES = [
  'requestHotels',
  'requestExperiences',
  'requestTemplates',
];

const CHILDREN_CONFIG = {
  requestHotels: {
    idKey: 'hotelId',
    labelKey: 'requestHotels',
    nameField: 'hotelName',
    icon: 'fa-regular fa-hotel text-indigo-500',
    fallbackPrefix: 'Hotel',
  },
  requestExperiences: {
    idKey: 'experienceId',
    labelKey: 'requestExperiences',
    nameField: 'experienceName',
    icon: 'fa-regular fa-layer-group text-blue-500',
    fallbackPrefix: 'Experience',
  },
  requestTemplates: {
    idKey: 'tripTemplateId',
    labelKey: 'requestTemplates',
    nameField: 'tripTemplateName',
    icon: 'fa-regular fa-file-lines text-purple-500',
    fallbackPrefix: 'Template',
  },
};

// Fields to compare on children items
const CHILDREN_DIFF_FIELDS = ['enabled', 'margin', 'isMarginPercent'];

// Fields to compare on period items within hotels
const PERIOD_DIFF_FIELDS = [
  'allotmentCount',
  'freeSale',
  'isPeriodAllowedForBooking',
  'margin',
  'isMarginPercent',
];

function getValueForField(snapshot, field) {
  if (!snapshot || !field) return undefined;

  const parts = field.split('.');
  let value = snapshot;

  for (const part of parts) {
    if (value === undefined || value === null) return undefined;
    value = value[part];
  }

  return value;
}

// Resolve item name from labels
function resolveItemName(childField, itemId, labels) {
  const config = CHILDREN_CONFIG[childField];
  if (!config) return null;
  const id = String(itemId);

  for (const source of [labels?.before, labels?.after]) {
    if (!source?.[config.labelKey]?.length) continue;
    const found = source[config.labelKey].find(
      (item) => String(item[config.idKey]) === id,
    );
    if (found?.[config.nameField]) return found[config.nameField];
  }
  return null;
}

// Resolve room name for a period from labels
function resolvePeriodRoomName(hotelId, periodId, labels) {
  const id = String(hotelId);
  const pid = String(periodId);

  for (const source of [labels?.before, labels?.after]) {
    if (!source?.requestHotels?.length) continue;
    const hotel = source.requestHotels.find((h) => String(h.hotelId) === id);
    if (!hotel?.periods?.length) continue;
    const period = hotel.periods.find((p) => String(p.periodId) === pid);
    if (period?.hotelRoomName) return period.hotelRoomName;
  }
  return null;
}

// Compare periods arrays for a hotel, returning added/removed/modified periods
function getPeriodsDiff(beforePeriods, afterPeriods) {
  const before = beforePeriods || [];
  const after = afterPeriods || [];

  const beforeMap = new Map();
  before.forEach((p) => {
    if (p.periodId != null) beforeMap.set(String(p.periodId), p);
  });

  const afterMap = new Map();
  after.forEach((p) => {
    if (p.periodId != null) afterMap.set(String(p.periodId), p);
  });

  const diff = [];

  // Removed periods
  beforeMap.forEach((beforePeriod, pid) => {
    if (!afterMap.has(pid)) {
      diff.push({ type: 'removed', periodId: pid, before: beforePeriod });
    }
  });

  // Added and modified periods
  afterMap.forEach((afterPeriod, pid) => {
    if (!beforeMap.has(pid)) {
      diff.push({ type: 'added', periodId: pid, after: afterPeriod });
    } else {
      const beforePeriod = beforeMap.get(pid);
      const changes = [];

      for (const field of PERIOD_DIFF_FIELDS) {
        if (
          beforePeriod[field] !== undefined &&
          afterPeriod[field] !== undefined &&
          String(beforePeriod[field]) !== String(afterPeriod[field])
        ) {
          changes.push({
            field,
            before: beforePeriod[field],
            after: afterPeriod[field],
          });
        }
      }

      if (changes.length > 0) {
        diff.push({
          type: 'modified',
          periodId: pid,
          before: beforePeriod,
          after: afterPeriod,
          changes,
        });
      }
    }
  });

  return diff;
}

// Build diff for a children array, returning only changed items
function getChildrenDiff(beforeSnapshot, afterSnapshot, childField, labels, t) {
  const config = CHILDREN_CONFIG[childField];
  if (!config) return [];

  const beforeItems = beforeSnapshot?.[childField] || [];
  const afterItems = afterSnapshot?.[childField] || [];
  const { idKey } = config;

  const beforeMap = new Map();
  beforeItems.forEach((item) => {
    const id = item[idKey];
    if (id != null) beforeMap.set(String(id), item);
  });

  const afterMap = new Map();
  afterItems.forEach((item) => {
    const id = item[idKey];
    if (id != null) afterMap.set(String(id), item);
  });

  const diff = [];

  // Removed items (in before, not in after)
  beforeMap.forEach((beforeItem, id) => {
    if (!afterMap.has(id)) {
      const name =
        resolveItemName(childField, id, labels) ||
        `${config.fallbackPrefix} #${id}`;
      diff.push({ type: 'removed', id, name, before: beforeItem });
    }
  });

  // Added and modified items
  afterMap.forEach((afterItem, id) => {
    const name =
      resolveItemName(childField, id, labels) ||
      `${config.fallbackPrefix} #${id}`;

    if (!beforeMap.has(id)) {
      diff.push({ type: 'added', id, name, after: afterItem });
    } else {
      const beforeItem = beforeMap.get(id);
      const changes = [];

      for (const field of CHILDREN_DIFF_FIELDS) {
        if (
          beforeItem[field] !== undefined &&
          afterItem[field] !== undefined &&
          String(beforeItem[field]) !== String(afterItem[field])
        ) {
          changes.push({
            field,
            before: beforeItem[field],
            after: afterItem[field],
          });
        }
      }

      // For requestHotels, also compare periods
      let periodChanges = [];
      if (childField === 'requestHotels') {
        periodChanges = getPeriodsDiff(beforeItem.periods, afterItem.periods);
      }

      if (changes.length > 0 || periodChanges.length > 0) {
        diff.push({
          type: 'modified',
          id,
          name,
          before: beforeItem,
          after: afterItem,
          changes,
          periodChanges,
        });
      }
    }
  });

  return diff;
}

// Render a child item card (hotel/experience/template)
function ChildItemCard({
  childField,
  item,
  beforeItem,
  afterItem,
  name,
  isModified,
  isNew,
  changes,
  periodChanges,
  labels,
  t,
}) {
  const config = CHILDREN_CONFIG[childField];
  const borderClass = isModified
    ? 'border-amber-200'
    : isNew
      ? 'border-green-300'
      : 'border-gray-200';

  return (
    <div className={`p-2 bg-gray-50 rounded border ${borderClass}`}>
      <div className="flex items-center gap-2 mb-1">
        <i className={config.icon} />
        <span className="text-sm font-medium text-gray-700">{name}</span>
        {isModified && (
          <span className="text-xs uppercase font-semibold text-k-orange">
            {t('modified', 'modified')}
          </span>
        )}
        {isNew && (
          <span className="text-xs uppercase font-semibold text-green-600">
            {t('added', 'added')}
          </span>
        )}
      </div>

      {/* Show field values summary */}
      {item && !changes && (
        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
          {item.margin !== undefined && (
            <div>
              {t('field.margin', 'Margin')}: {item.margin}
              {item.isMarginPercent ? '%' : ''}
            </div>
          )}
          {item.enabled !== undefined && (
            <div>
              {item.enabled
                ? t('enabled', 'Enabled')
                : t('disabled', 'Disabled')}
            </div>
          )}
        </div>
      )}

      {/* Show changes for modified items */}
      {changes?.length > 0 && (
        <div className="space-y-1 mt-2">
          {changes.map((change) => (
            <div
              key={change.field}
              className="flex items-center space-x-2 text-sm"
            >
              <span className="text-gray-500 font-medium shrink-0">
                {formatChangeFieldLabel(change.field, t)}:
              </span>
              <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs">
                {formatChangeValue(change.field, change.before, t, {
                  isMarginPercent: beforeItem?.isMarginPercent,
                })}
              </span>
              <i className="fa-solid fa-arrow-right text-xs text-gray-400" />
              <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded text-xs">
                {formatChangeValue(change.field, change.after, t, {
                  isMarginPercent: afterItem?.isMarginPercent,
                })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Show period-level room changes for hotels */}
      {periodChanges?.length > 0 && (
        <div className="mt-2 space-y-2">
          {groupPeriodChangesByRoom(periodChanges, item?.hotelId, labels).map(
            ({ hotelRoomName, periods }) => (
              <div
                key={hotelRoomName}
                className="border border-gray-200 rounded p-2 bg-white"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <i className="fa-regular fa-door-open text-gray-400 text-xs" />
                  <span className="text-xs font-medium text-gray-600">
                    {hotelRoomName}
                  </span>
                </div>
                <div className="space-y-1">
                  {periods.map((period) => (
                    <div key={period.periodId}>
                      {period.type === 'added' && (
                        <div className="text-xs text-green-600">
                          <span className="uppercase font-semibold">
                            {t('added', 'added')}
                          </span>
                          {period.after?.allotmentCount !== undefined && (
                            <span className="ml-1 text-gray-500">
                              — {t('allotment', 'Allotment')}:{' '}
                              {period.after.allotmentCount}
                            </span>
                          )}
                        </div>
                      )}
                      {period.type === 'removed' && (
                        <div className="text-xs text-red-600">
                          <span className="uppercase font-semibold">
                            {t('removed', 'removed')}
                          </span>
                        </div>
                      )}
                      {period.type === 'modified' &&
                        period.changes?.map((change) => (
                          <div
                            key={change.field}
                            className="flex items-center space-x-2 text-xs"
                          >
                            <span className="text-gray-500 font-medium shrink-0">
                              {formatChangeFieldLabel(change.field, t)}:
                            </span>
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded">
                              {formatChangeValue(
                                change.field,
                                change.before,
                                t,
                                {
                                  isMarginPercent:
                                    period.before?.isMarginPercent,
                                },
                              )}
                            </span>
                            <i
                              className="fa-solid fa-arrow-right text-gray-400"
                              style={{ fontSize: '0.6rem' }}
                            />
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-600 rounded">
                              {formatChangeValue(
                                change.field,
                                change.after,
                                t,
                                {
                                  isMarginPercent:
                                    period.after?.isMarginPercent,
                                },
                              )}
                            </span>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {/* Show period summary for newly added hotels */}
      {isNew && item?.periods?.length > 0 && (
        <div className="mt-2 space-y-1">
          {groupPeriodsForSummary(item.periods, item.hotelId, labels).map(
            ({ hotelRoomName, periods }) => (
              <div
                key={hotelRoomName}
                className="text-xs text-gray-500 flex items-center gap-1.5"
              >
                <i
                  className="fa-regular fa-door-open text-gray-400"
                  style={{ fontSize: '0.6rem' }}
                />
                <span>{hotelRoomName}</span>
                <span>
                  — {t('allotment', 'Allotment')}:{' '}
                  {periods.map((p) => p.allotmentCount).join(', ')}
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// Group period changes by room name for display
function groupPeriodChangesByRoom(periodChanges, hotelId, labels) {
  const groups = new Map();

  periodChanges.forEach((period) => {
    const periodData = period.after || period.before;
    const roomName =
      resolvePeriodRoomName(hotelId, period.periodId, labels) ||
      `Room #${periodData?.hotelRoomId || '?'}`;

    if (!groups.has(roomName)) {
      groups.set(roomName, []);
    }
    groups.get(roomName).push(period);
  });

  return Array.from(groups.entries()).map(([hotelRoomName, periods]) => ({
    hotelRoomName,
    periods,
  }));
}

// Group periods for summary display on newly added hotels
function groupPeriodsForSummary(periods, hotelId, labels) {
  const groups = new Map();

  periods.forEach((period) => {
    const roomName =
      resolvePeriodRoomName(hotelId, period.periodId, labels) ||
      `Room #${period.hotelRoomId || '?'}`;

    if (!groups.has(roomName)) {
      groups.set(roomName, []);
    }
    groups.get(roomName).push(period);
  });

  return Array.from(groups.entries()).map(([hotelRoomName, periods]) => ({
    hotelRoomName,
    periods,
  }));
}

function formatChangeFieldLabel(field, t) {
  if (field === 'enabled') return t('enabled', 'Enabled');
  if (field === 'margin') return t('field.margin', 'Margin');
  if (field === 'isMarginPercent')
    return t('field.isMarginPercent', 'Margin Type');
  if (field === 'allotmentCount') return t('allotment', 'Allotment');
  if (field === 'freeSale') return t('freeSale', 'Free Sale');
  if (field === 'isPeriodAllowedForBooking')
    return t('isPeriodAllowedForBooking', 'Bookable');
  return field;
}

function formatChangeValue(field, value, t, { isMarginPercent } = {}) {
  if (field === 'enabled') {
    return value ? t('enabled', 'Enabled') : t('disabled', 'Disabled');
  }
  if (field === 'isMarginPercent') {
    return value
      ? t('percentage', 'Percentage')
      : t('fixedAmount', 'Fixed amount');
  }
  if (field === 'margin') {
    return `${String(value)}${isMarginPercent ? '%' : ''}`;
  }
  if (field === 'freeSale' || field === 'isPeriodAllowedForBooking') {
    return value ? t('yes', 'Yes') : t('no', 'No');
  }
  return String(value);
}

export default function ToConnectionRequestHistoryCard({
  history,
  fieldLabels = {},
}) {
  const { t } = useTranslation('toConnectionRequest');
  const [expanded, setExpanded] = useState(false);

  const {
    action,
    beforeSnapshot,
    afterSnapshot,
    changedFields = [],
    userFullName,
    organizationApiKeyName,
    createdAt,
    labels = {},
  } = history;

  // Filter out ignored fields and fields where only time seconds differ
  const filteredChangedFields = useMemo(() => {
    if (!changedFields) return [];
    return changedFields.filter((field) => {
      const fieldParts = field.split('.');
      const lastPart = fieldParts[fieldParts.length - 1];

      if (IGNORED_FIELDS.includes(lastPart)) {
        return false;
      }

      if (lastPart === 'arrivalTime' || lastPart === 'departureTime') {
        const beforeVal = getValueForField(beforeSnapshot, field);
        const afterVal = getValueForField(afterSnapshot, field);
        const normalizedBefore = normalizeTimeValue(beforeVal);
        const normalizedAfter = normalizeTimeValue(afterVal);
        if (normalizedBefore === normalizedAfter) {
          return false;
        }
      }

      return true;
    });
  }, [changedFields, beforeSnapshot, afterSnapshot]);

  // Separate top-level fields from children fields
  const topLevelFields = useMemo(() => {
    return filteredChangedFields.filter(
      (field) => !CHILDREN_PREFIXES.some((p) => field.startsWith(p)),
    );
  }, [filteredChangedFields]);

  // Deduplicate children paths to root array field names
  const childrenArrayFields = useMemo(() => {
    const fields = new Set();
    filteredChangedFields.forEach((field) => {
      const prefix = CHILDREN_PREFIXES.find((p) => field.startsWith(p));
      if (prefix) fields.add(prefix);
    });
    return Array.from(fields);
  }, [filteredChangedFields]);

  // Build detailed children diffs
  const childrenDiffs = useMemo(() => {
    const diffs = [];
    for (const childField of childrenArrayFields) {
      const items = getChildrenDiff(
        beforeSnapshot,
        afterSnapshot,
        childField,
        labels,
        t,
      );
      if (items.length > 0) {
        diffs.push({ childField, items });
      }
    }
    return diffs;
  }, [childrenArrayFields, beforeSnapshot, afterSnapshot, labels, t]);

  // All field badges for collapsed view
  const allFieldBadges = useMemo(
    () => [...topLevelFields, ...childrenArrayFields],
    [topLevelFields, childrenArrayFields],
  );

  const hasExpandableContent =
    topLevelFields.length > 0 || childrenDiffs.length > 0;

  const authorName = userFullName || organizationApiKeyName || 'KOOB';
  const isApiKey = organizationApiKeyName && !userFullName;
  const color = actionColors[action] || actionColors.update;
  const expandable = action === 'update';

  const getFieldLabel = (field) => {
    if (fieldLabels[field]) return fieldLabels[field];

    return field
      .split('.')
      .pop()
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Render collapsed card content based on action
  const renderCollapsedContent = () => {
    if (action === 'create') {
      const connectionType = afterSnapshot?.connectionType;
      const state = afterSnapshot?.state;
      return (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {t('created', 'Created')} -{' '}
            {connectionType &&
              t(`connectionType.${connectionType}`, connectionType)}
            {state && ` (${t(`state.${state}`, state)})`}
          </span>
        </div>
      );
    }

    if (action === 'delete') {
      return (
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">
            {t('deleted', 'Deleted')}
          </span>
        </div>
      );
    }

    // For update action, show field badges
    return (
      <>
        {allFieldBadges?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {allFieldBadges.slice(0, 3).map((field) => (
              <span
                key={field}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600"
              >
                {getFieldLabel(field)}
              </span>
            ))}
            {allFieldBadges.length > 3 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                +{allFieldBadges.length - 3} {t('more', 'more')}
              </span>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="px-5 py-3 rounded-lg bg-gray-50 border">
      <div
        className={[
          'flex justify-between items-start',
          expandable ? 'cursor-pointer' : '',
        ].join(' ')}
        onClick={() => {
          if (!expandable) return;
          setExpanded(!expanded);
        }}
      >
        <div className="flex-1">
          <div className="flex items-center space-x-3">
            <i className="fa-solid fa-circle text-xs" style={{ color }} />

            <h3
              className="font-semibold tracking-wide capitalize"
              style={{ color }}
            >
              {t(`action.${action}`, action)}
            </h3>
          </div>

          {renderCollapsedContent()}

          <div className="flex items-center space-x-2 text-gray-500 mt-3">
            <i className="fa-regular fa-user h-4 w-4" />
            <span className="text-sm">{authorName}</span>
            {isApiKey && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                API
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end items-center space-x-3">
          <span className="text-gray-500 text-sm">
            {formatDateTime(createdAt, 'D MMM YYYY, HH:mm')}
          </span>

          {expandable && (
            <div>
              {expanded ? (
                <i className="fa-regular fa-chevron-up w-5 h-5 text-gray-400" />
              ) : (
                <i className="fa-regular fa-chevron-down w-5 h-5 text-gray-400" />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail view with before/after grid */}
      {expanded && hasExpandableContent && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-[1fr,1fr] gap-x-4 mb-3">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {t('before', 'Before')}
            </h4>
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              {t('after', 'After')}
            </h4>
          </div>
          <div className="space-y-3">
            {/* Top-level fields */}
            {topLevelFields.map((field) => (
              <div key={field}>
                <div className="text-sm font-medium text-gray-700 mb-1">
                  {getFieldLabel(field)}
                </div>
                <div className="grid grid-cols-[1fr,1fr] gap-x-4 bg-white rounded-lg border">
                  <div className="p-3 border-r">
                    {renderFieldValue(
                      field,
                      getValueForField(beforeSnapshot, field),
                      labels?.before,
                      beforeSnapshot,
                      t,
                    )}
                  </div>
                  <div className="p-3">
                    {renderFieldValue(
                      field,
                      getValueForField(afterSnapshot, field),
                      labels?.after,
                      afterSnapshot,
                      t,
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Children array fields - only changed items, inside before/after grid */}
            {childrenDiffs.map(({ childField, items }) => (
              <div key={childField}>
                <div className="text-sm font-medium text-gray-700 mb-1">
                  {getFieldLabel(childField)}
                </div>
                <div className="bg-white rounded-lg border divide-y">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-[1fr,1fr] gap-x-4"
                    >
                      {/* Before column */}
                      <div className="p-3 border-r">
                        {item.type === 'added' ? (
                          <span className="text-gray-400 italic">-</span>
                        ) : (
                          <ChildItemCard
                            childField={childField}
                            item={item.before}
                            beforeItem={item.before}
                            afterItem={item.after}
                            name={item.name}
                            labels={labels}
                            t={t}
                          />
                        )}
                      </div>
                      {/* After column */}
                      <div className="p-3">
                        {item.type === 'removed' ? (
                          <div className="p-2 bg-gray-50 rounded border border-red-200 opacity-60">
                            <div className="flex items-center gap-2">
                              <i className={CHILDREN_CONFIG[childField].icon} />
                              <span className="text-sm font-medium text-gray-700 line-through">
                                {item.name}
                              </span>
                              <span className="text-xs uppercase font-semibold text-red-600">
                                {t('removed', 'removed')}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <ChildItemCard
                            childField={childField}
                            item={item.after}
                            beforeItem={item.before}
                            afterItem={item.after}
                            name={item.name}
                            isModified={item.type === 'modified'}
                            isNew={item.type === 'added'}
                            changes={item.changes}
                            periodChanges={item.periodChanges}
                            labels={labels}
                            t={t}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper to render field values with custom formatting
function renderFieldValue(field, value, labels, snapshot, t) {
  if (value === undefined || value === null) {
    return <span className="text-gray-400 italic">-</span>;
  }

  // State field with colored badges
  if (field === 'state') {
    const stateColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-green-100 text-green-700',
      refused: 'bg-red-100 text-red-700',
    };
    const colorClass = stateColors[value] || 'bg-gray-100 text-gray-700';
    return (
      <span className={`px-2 py-1 text-sm rounded-full ${colorClass}`}>
        {value}
      </span>
    );
  }

  // isMarginPercent - show as Percentage / Fixed amount instead of Yes/No
  if (field === 'isMarginPercent') {
    return (
      <span className="text-sm">
        {value
          ? t('percentage', 'Percentage')
          : t('fixedAmount', 'Fixed amount')}
      </span>
    );
  }

  // Margin field - append % when isMarginPercent is true
  if (
    field === 'margin' &&
    (typeof value === 'number' || typeof value === 'string')
  ) {
    return (
      <span className="text-sm">
        {String(value)}
        {snapshot?.isMarginPercent ? '%' : ''}
      </span>
    );
  }

  // Boolean fields as Yes/No
  if (typeof value === 'boolean') {
    return <span className="text-sm">{value ? 'Yes' : 'No'}</span>;
  }

  // Organization fields - resolve from labels if available
  if (
    (field === 'toOrganizationId' || field === 'dmcOrganizationId') &&
    labels
  ) {
    const orgName = labels[field];
    if (orgName) {
      return <span className="text-sm">{orgName}</span>;
    }
  }

  // Default rendering for other types
  if (typeof value === 'string' || typeof value === 'number') {
    return <span className="text-sm">{String(value)}</span>;
  }

  // For complex objects, stringify
  return <span className="text-sm text-gray-600">{JSON.stringify(value)}</span>;
}
